import type { Location, UIMatch } from 'react-router-dom';

export interface AppRouteHandle {
  readonly title: string;
  readonly breadcrumb?: string;
  readonly listPath?: string;
  readonly navigationKey?: string;
}

export interface AppNavigationState {
  readonly analyticsConsumptionSource?: AnalyticsConsumptionSource | undefined;
  readonly restoreScrollKey?: string;
  readonly returnScrollKey?: string;
  readonly returnTo?: string;
}

export interface AnalyticsConsumptionSource {
  readonly endDate?: string;
  readonly packageTemplateId?: string;
  readonly primaryCoachEmployeeId?: string;
  readonly startDate?: string;
  readonly studentId?: string;
  readonly trendMetric: AnalyticsTrendMetric;
}

type AnalyticsTrendMetric =
  'CONSUMPTION_VALUE' | 'CONSUMED_LESSON_UNITS' | 'CONSUMPTION_COUNT' | 'STUDENT_COUNT';

export const appRouteHandles = {
  purchaseActivity: listHandle('/purchases/activity', '新增购课记录'),
  analyticsConsumption: listHandle('/analytics', '数据分析'),
  accountPassword: routeHandle({ title: '账号安全' }),
  auditList: listHandle('/audits', '审计与对账'),
  commissionDetail: detailHandle('/commissions', '分成明细'),
  commissionList: listHandle('/commissions', '教练分成'),
  salesCommissionList: listHandle('/sales-commissions', '前台销售分成'),
  commissionPlanList: listHandle('/commission-plans', '分成公式与计划'),
  consumptionCreate: detailHandle('/consumptions', '录入课销'),
  consumptionDetail: detailHandle('/consumptions', '课销批次详情'),
  consumptionList: listHandle('/consumptions', '课销'),
  dailyTaskEvidenceList: listHandle('/daily-task-evidence', '完成依据'),
  dailyTaskInstanceDetail: detailHandle('/dashboard', '日常待办详情'),
  dailyTaskReviewList: listHandle('/daily-task-reviews', '待办审核'),
  dailyTaskTemplateList: listHandle('/daily-task-templates', '任务模板'),
  dashboard: listHandle('/dashboard', '工作台'),
  employeeDetail: detailHandle('/employees', '员工详情'),
  employeeList: listHandle('/employees', '员工管理'),
  exportList: listHandle('/exports', '导出任务'),
  formulaList: listHandle('/commission-formulas', '分成管理'),
  login: routeHandle({ breadcrumb: '登录', title: '登录' }),
  packageTemplateDetail: detailHandle('/package-templates', '课包详情'),
  packageTemplateList: listHandle('/package-templates', '课包管理'),
  studentDetail: detailHandle('/students', '学员详情'),
  studentList: listHandle('/students', '学员管理'),
} as const satisfies Readonly<Record<string, AppRouteHandle>>;

const returnSourceLabels = new Map<string, string>([
  ['/purchases/activity', '新增购课记录'],
  ['/audits', appRouteHandles.auditList.title],
  ['/analytics', '数据分析'],
  ['/analytics/consumption', '数据分析'],
  ['/commissions', appRouteHandles.commissionList.title],
  ['/sales-commissions', appRouteHandles.salesCommissionList.title],
  ['/commission-plans', appRouteHandles.commissionPlanList.title],
  ['/consumptions', appRouteHandles.consumptionList.title],
  ['/dashboard', appRouteHandles.dashboard.title],
  ['/employees', appRouteHandles.employeeList.title],
  ['/package-templates', appRouteHandles.packageTemplateList.title],
  ['/students', appRouteHandles.studentList.title],
]);

export function createReturnNavigationState(
  location: Pick<Location, 'key' | 'pathname' | 'search' | 'state'>,
): AppNavigationState | undefined {
  if (!returnSourceLabel(location.pathname)) return undefined;
  return {
    returnScrollKey: scrollRestorationKey(location),
    returnTo: `${location.pathname}${location.search}`,
  };
}

export function resolveReturnTarget(
  state: unknown,
  fallbackListPath: string,
): { readonly label: string; readonly path: string; readonly scrollKey?: string } {
  const fallbackLabel = returnSourceLabels.get(fallbackListPath);
  if (!fallbackLabel) throw new Error(`Unknown fallback list path: ${fallbackListPath}`);
  const navigationState = appNavigationState(state);
  const returnPath = safeReturnPath(navigationState?.returnTo);
  if (!returnPath) return { label: fallbackLabel, path: fallbackListPath };
  const pathname = new URL(returnPath, 'https://badminton.invalid').pathname;
  return {
    label: returnSourceLabel(pathname) ?? fallbackLabel,
    path: returnPath,
    ...(isScrollKey(navigationState?.returnScrollKey)
      ? { scrollKey: navigationState.returnScrollKey }
      : {}),
  };
}

export function scrollRestorationKey(location: Pick<Location, 'key' | 'state'>): string {
  const navigationState = appNavigationState(location.state);
  return isScrollKey(navigationState?.restoreScrollKey)
    ? navigationState.restoreScrollKey
    : location.key;
}

export function createRestoreNavigationState(scrollKey: unknown): AppNavigationState | undefined {
  return isScrollKey(scrollKey) ? { restoreScrollKey: scrollKey } : undefined;
}

export function createAnalyticsConsumptionReturnState(
  location: Pick<Location, 'key' | 'pathname' | 'search' | 'state'>,
  source: AnalyticsConsumptionSource,
): AppNavigationState {
  return {
    analyticsConsumptionSource: source,
    returnScrollKey: scrollRestorationKey(location),
    returnTo: `${location.pathname}${location.search}`,
  };
}

export function analyticsConsumptionSource(value: unknown): AnalyticsConsumptionSource | undefined {
  return appNavigationState(value)?.analyticsConsumptionSource;
}

export function navigationKeyFromMatches(matches: readonly UIMatch[]): string | undefined {
  return [...matches]
    .reverse()
    .map((match) => appRouteHandle(match.handle)?.navigationKey)
    .find((key) => key !== undefined);
}

export function appNavigationState(value: unknown): AppNavigationState | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const restoreScrollKey = 'restoreScrollKey' in value ? value.restoreScrollKey : undefined;
  const returnScrollKey = 'returnScrollKey' in value ? value.returnScrollKey : undefined;
  const returnTo = 'returnTo' in value ? value.returnTo : undefined;
  const source =
    'analyticsConsumptionSource' in value ? value.analyticsConsumptionSource : undefined;
  const state: AppNavigationState = {
    ...(parseAnalyticsConsumptionSource(source)
      ? { analyticsConsumptionSource: parseAnalyticsConsumptionSource(source) }
      : {}),
    ...(isScrollKey(restoreScrollKey) ? { restoreScrollKey } : {}),
    ...(isScrollKey(returnScrollKey) ? { returnScrollKey } : {}),
    ...(typeof returnTo === 'string' ? { returnTo } : {}),
  };
  return Object.keys(state).length > 0 ? state : undefined;
}

export function appRouteHandle(value: unknown): AppRouteHandle | undefined {
  if (typeof value !== 'object' || value === null || !('title' in value)) return undefined;
  if (typeof value.title !== 'string') return undefined;
  const breadcrumb = 'breadcrumb' in value ? value.breadcrumb : undefined;
  const listPath = 'listPath' in value ? value.listPath : undefined;
  const navigationKey = 'navigationKey' in value ? value.navigationKey : undefined;
  return {
    title: value.title,
    ...(typeof breadcrumb === 'string' ? { breadcrumb } : {}),
    ...(typeof listPath === 'string' ? { listPath } : {}),
    ...(typeof navigationKey === 'string' ? { navigationKey } : {}),
  };
}

function routeHandle(handle: AppRouteHandle): AppRouteHandle {
  return handle;
}

function listHandle(path: string, title: string): AppRouteHandle {
  return routeHandle({ navigationKey: path, title });
}

function detailHandle(listPath: string, title: string): AppRouteHandle {
  return routeHandle({ breadcrumb: title, listPath, navigationKey: listPath, title });
}

function safeReturnPath(value: unknown): string | undefined {
  if (
    typeof value !== 'string' ||
    value.length > 2_048 ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    value.includes('#')
  ) {
    return undefined;
  }
  try {
    const url = new URL(value, 'https://badminton.invalid');
    if (url.origin !== 'https://badminton.invalid' || !returnSourceLabel(url.pathname)) {
      return undefined;
    }
    return `${url.pathname}${url.search}`;
  } catch {
    return undefined;
  }
}

function returnSourceLabel(pathname: string): string | undefined {
  const listLabel = returnSourceLabels.get(pathname);
  if (listLabel) return listLabel;
  if (/^\/package-templates\/[A-Za-z0-9_-]{1,128}$/u.test(pathname)) return '课包详情';
  return /^\/students\/[A-Za-z0-9_-]{1,128}$/u.test(pathname) ? '学员详情' : undefined;
}

function parseAnalyticsConsumptionSource(value: unknown): AnalyticsConsumptionSource | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const startDate = 'startDate' in value ? naturalDate(value.startDate) : undefined;
  const endDate = 'endDate' in value ? naturalDate(value.endDate) : undefined;
  const packageTemplateId =
    'packageTemplateId' in value ? stableIdentifier(value.packageTemplateId) : undefined;
  const primaryCoachEmployeeId =
    'primaryCoachEmployeeId' in value ? stableIdentifier(value.primaryCoachEmployeeId) : undefined;
  const studentId = 'studentId' in value ? stableIdentifier(value.studentId) : undefined;
  const trendMetric = 'trendMetric' in value ? value.trendMetric : undefined;
  if (!isAnalyticsTrendMetric(trendMetric)) return undefined;
  if ((startDate === undefined) !== (endDate === undefined)) return undefined;
  return {
    ...(endDate !== undefined ? { endDate } : {}),
    ...(packageTemplateId !== undefined ? { packageTemplateId } : {}),
    ...(primaryCoachEmployeeId !== undefined ? { primaryCoachEmployeeId } : {}),
    ...(startDate !== undefined ? { startDate } : {}),
    ...(studentId !== undefined ? { studentId } : {}),
    trendMetric,
  };
}

function naturalDate(value: unknown): string | undefined {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/u.test(value) ? value : undefined;
}

function stableIdentifier(value: unknown): string | undefined {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/u.test(value) ? value : undefined;
}

function isAnalyticsTrendMetric(value: unknown): value is AnalyticsTrendMetric {
  return (
    value === 'CONSUMPTION_VALUE' ||
    value === 'CONSUMED_LESSON_UNITS' ||
    value === 'CONSUMPTION_COUNT' ||
    value === 'STUDENT_COUNT'
  );
}

function isScrollKey(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/u.test(value);
}
